import React, { useCallback, useMemo } from 'react'
import ShellSelectCreatableFetched from '@e3dc-react/shell/Elements/Select/SelectCreatableFetched'
import { useField, useFormikContext } from 'formik'
import { LabeledOption } from '@e3dc-react/shell/Types/globalTypes'

interface SelectProps<T> {
  url: string
  label: string
  name: string
  mapFunction: (item: T) => LabeledOption<string>
  filterByParameter: keyof T
  disabled?: boolean
  className?: string
  required?: boolean
  clearable?: boolean
  minCharactersToStartSearch?: number
}

/**
 * This component makes the (shell) SelectCreatableFetched component compatible with Formik
 */
const SelectCreatableFetched = <T extends Record<string, unknown>>({
  url,
  label,
  name,
  mapFunction,
  filterByParameter,
  disabled = false,
  className = '',
  clearable = false,
  required = false,
  minCharactersToStartSearch = 0,
}: SelectProps<T>): React.ReactElement => {
  const [field, meta] = useField(name)
  const { setFieldValue } = useFormikContext()
  const value: LabeledOption<string> = { label: field.value, value: field.value }

  const isClearable = useMemo(() => meta.value && clearable, [meta.value, clearable])

  const handleChange = useCallback(
    (event: unknown) => {
      setFieldValue(name, event)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name]
  )

  return (
    <ShellSelectCreatableFetched<T>
      label={label}
      filterByParameter={filterByParameter}
      mapFunction={mapFunction}
      onSelect={handleChange}
      url={url}
      value={value}
      className={className}
      disabled={disabled}
      clearable={isClearable}
      required={required}
      minCharactersToStartSearch={minCharactersToStartSearch}
    />
  )
}

export default SelectCreatableFetched
